<template>
  <div id="list-licenses" class="container-fluid">
    <!-- License Filters -->
    <b-form
      id="licenses-form"
      name="licensesForm"
      class="pt-1 page-form"
      autocomplete="off">
      <input autocomplete="false" name="hidden" type="text" style="display:none;">
      <div class="row">
        <!-- Filters -->
        <div
          id="license-filters"
          class="col page-filters">
          <div class="row">
            <!-- General Text Filter -->
            <div class="col-3 pr-0">
              <label for="filter-text">
                Filter Licenses:
                <font-awesome-icon
                  icon="info-circle"
                  v-b-tooltip="{
                    title:`Filters licenses by
                    Customer, Company, Access Code, Device Limit, and Refresh.`,
                    placement:'bottom',
                    delay:{'show':500}
                  }">
                </font-awesome-icon>
              </label>

              <b-input-group size="sm">
                <b-form-input
                  id="filter-text"
                  type="text"
                  name="filterText"
                  placeholder="Type any filter text"
                  v-model="filters.text"
                />
                <b-input-group-append>
                  <b-button
                    :disabled="!filters.text"
                    @click="filters.text = '';">
                    <font-awesome-icon icon="times"></font-awesome-icon>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
            <!-- / General Text Filter -->

            <!-- License Status -->
            <div class="col-3 pr-0">
              <label>
                Show:
              </label>
              <b-form-radio-group
                class="d-flex align-items-center h-31"
                name="licenseStatus"
                v-model="filters.status">
                <b-form-radio value="active">Active</b-form-radio>

                <b-form-radio
                  value="inactive">
                  Expired
                </b-form-radio>

                <b-form-radio value="both">Both</b-form-radio>
              </b-form-radio-group>
            </div>
            <!-- / License Status -->

            <!-- Over Activations -->
            <div class="col-3 pr-0">
              <label>&nbsp;</label><br>
              <b-form-checkbox
                class="d-flex align-items-center h-31"
                v-model="filters.overActivations">
                Licenses over activation limit
              </b-form-checkbox>
            </div>
            <!-- Over Activations -->
          </div>

          <div class="row">
            <!-- Date Range -->
            <div class="col-3 pr-0 mt-3">
              <b-form-radio-group
                size="sm"
                name="dateBetween"
                buttons
                button-variant="outline-primary"
                v-model="filters.dateBetween">
                <b-form-radio class="mr-1 p-1" value="StartingDate">Issued</b-form-radio>
                <b-form-radio class="p-1" value="ExpirationDate">Expires</b-form-radio>
                <b-form-radio
                  class="p-1 text-body label-radio"
                  disabled>
                  Between:
                </b-form-radio>
              </b-form-radio-group>

              <div class="d-flex mt-1">
                <b-form-input
                  size="sm"
                  class="mr-1 w-50"
                  v-model="filters.fromDate"
                  type="date">
                </b-form-input>

                <b-form-input
                  size="sm"
                  class="w-50"
                  v-model="filters.toDate"
                  type="date">
                </b-form-input>
              </div>
            </div>
            <!-- / Date Range -->
          </div>
        </div>
        <!-- / Filters -->

        <!-- Actions -->
        <div
          id="license-actions"
          class="col-auto d-flex align-items-end page-actions">
          <b-dropdown
            right
            size="sm"
            text="Actions">
            <b-dropdown-item
              class="p-2"
              @click="$router.push({ name: 'new-license' })">
              <font-awesome-icon
                icon="plus-circle"
                class="mr-1">
              </font-awesome-icon>
              Create New License
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              :disabled="selectedLicenses.length !== 1"
              @click="viewLicense(selectedLicenses[0])">
              <font-awesome-icon
                icon="pen"
                class="mr-1">
              </font-awesome-icon>
              Edit License
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              :disabled="selectedLicenses.length === 0"
              @click="revokeLicenses">
              <font-awesome-icon
                icon="times-circle"
                class="mr-1">
              </font-awesome-icon>
              Revoke License
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <!-- / Actions -->
      </div>
    </b-form>
    <!-- / License Filters -->

    <!-- License List -->
    <div
      id="license-results"
      class="results-list">
      <b-table
        class="table-fixed"
        v-table-lines
        striped
        hover
        selectable
        select-mode="range"
        @row-dblclicked="viewLicense"
        @row-selected="selectLicenses"
        :sort-compare="sort"
        :items="filteredLicenses"
        :fields="licenseFields"
        :show-empty="true"
        empty-text="There are no licenses to show."
        empty-filtered-text="There are no licenses to show.">
        <!-- Custom Name -->
        <template
          slot="Customer.LastName"
          slot-scope="data">
          {{data.item.Customer.LastName}}, {{data.item.Customer.FirstName}}
        </template>
        <!-- / Custom Name -->

        <!-- Custom Activations -->
        <template
          slot="Usage.length"
          slot-scope="data">
          <span
            :class="{
              'text-danger':data.item.Usage.length > data.item.DeviceLimit
            }">
            {{data.item.Usage.length}}
          </span>
        </template>
        <!-- / Custom Activations -->

        <!-- Custom Expiration -->
        <template
          slot="ExpirationDate"
          slot-scope="data">
          <span
            :class="{
              'text-danger':data.item.ExpirationDate.isBefore(),
            }">
            {{data.item.ExpirationDate.format('M/D/YYYY')}}
          </span>
        </template>
        <!-- / Custom Expiration -->
      </b-table>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import DataSvc from '../services/data';
import mixins from '../services/mixins';
import {
  EventBus,
  GlobalLoaderMixin,
} from '../services/event-bus';

export default {
  name: 'list-licenses',
  mixins: [
    GlobalLoaderMixin,
    mixins,
  ],
  data() {
    return {
      // filters form
      filters: {
        text: '',
        status: 'active',
        dateBetween: 'StartingDate',
        overActivations: false,
      },

      // license list
      licenses: [],
      selectedLicenses: [],
      licenseFields: [
        {
          label: 'Customer',
          key: 'Customer.LastName',
          sortable: true,
          class: 'license-name ellipsis',
        },
        {
          label: 'Company',
          key: 'Customer.Company',
          sortable: true,
          class: 'license-company ellipsis',
        },
        {
          label: 'Access Code',
          key: 'AccessCode',
          sortable: true,
          class: 'license-access-code ellipsis text-uppercase',
        },
        {
          label: 'Activations',
          key: 'Usage.length',
          sortable: true,
          class: 'license-activations ellipsis',
          tdClass: 'text-right',
        },
        {
          label: 'Device Limit',
          key: 'DeviceLimit',
          sortable: true,
          class: 'license-device-limit ellipsis',
          tdClass: 'text-right',
        },
        {
          label: 'Refresh (Days)',
          key: 'RefreshInterval',
          sortable: true,
          class: 'license-refresh ellipsis',
          tdClass: 'text-right',
        },
        {
          label: 'Issued',
          key: 'StartingDate',
          sortable: true,
          class: 'license-issued ellipsis',
          tdClass: 'text-right',
          formatter: m => m.format('M/D/YYYY'),
        },
        {
          label: 'Expires',
          key: 'ExpirationDate',
          sortable: true,
          class: 'license-valid-thru ellipsis',
          tdClass: 'text-right',
          formatter: m => m.format('M/D/YYYY'),
        },
      ],
    };
  },
  computed: {
    filteredLicenses() {
      const filterText = this.filters.text.toLowerCase();
      const text = l => l.Customer.FirstName.toLowerCase().includes(filterText)
        || l.Customer.LastName.toLowerCase().includes(filterText)
        || l.Customer.Company.toLowerCase().includes(filterText)
        || (l.Customer.Email || '').toLowerCase().includes(filterText)
        || (l.Customer.Phone || '').toLowerCase().includes(filterText)
        || !filterText;

      const status = l => (this.filters.status === 'active'
          && l.ExpirationDate.isSameOrAfter())
        || (this.filters.status === 'inactive'
          && l.ExpirationDate.isBefore())
        || this.filters.status === 'both';

      const activation = l => (this.filters.overActivations && l.Usage.length > l.DeviceLimit)
        || !this.filters.overActivations;

      const date = prop => l => ((this.filters.fromDate
          && !this.filters.toDate
          && l[prop].isSameOrAfter(this.filters.fromDate))
        || (this.filters.fromDate
          && this.filters.toDate
          && l[prop].isBetween(this.filters.fromDate, this.filters.toDate, null, '[]'))
        || (!this.filters.fromDate
          && this.filters.toDate
          && l[prop].isSameOrBefore(this.filters.toDate))
        || (!this.filters.fromDate && !this.filters.toDate));

      return _.chain(this.licenses)
        .filter(text)
        .filter(status)
        .filter(activation)
        .filter(date(this.filters.dateBetween))
        .value();
    },
  },
  methods: {
    revokeLicenses() {
      const plural = this.selectedLicenses.length > 1;
      const confirmMsg = `Are you sure you want to revoke
        ${plural ? 'these licenses' : 'this license'}?`;

      const revokeConfirmed = () => {
        this.showGlobalLoader(`Revoking license${plural ? 's' : ''}...`);

        return DataSvc.revokeLicenses(this.selectedLicenses)
          .then(this.getLicenses)
          .finally(() => {
            this.hideGlobalLoader();
          });
      };

      return this.showConfirm(confirmMsg).then(revokeConfirmed);
    },
    getLicenses() {
      return DataSvc.getLicenses()
        .then((licenses) => {
          licenses.forEach((l) => {
            l.StartingDate = moment(l.StartingDate.replace('Z', ''));
            l.ExpirationDate = moment(l.ExpirationDate.replace('Z', ''));

            if (!l.Usage) {
              l.Usage = [];
            }
          });

          this.licenses = licenses;

          this.$nextTick(() => {
            EventBus.$emit('table-data-loaded');
          });
        });
    },
    viewLicense(license) {
      this.$router.push({ name: 'view-license', params: license });
    },
    selectLicenses(licenses) {
      this.selectedLicenses = licenses;
    },
  },
  created() {
    this.showGlobalLoader('Loading licenses...');
    this.getLicenses()
      .finally(() => {
        this.hideGlobalLoader();
      });
  },
};
</script>

<style lang="scss">
#license-results {
  height: calc(100% - 139px - 2rem);
  margin: 1rem 0;

  table.table-fixed {
    th,
    td {
      &.license-name {
        width: calc((100% - 5 * 150px) / 3);
      }

      &.license-company {
        width: calc((100% - 5 * 150px) / 3);
      }

      &.license-access-code {
        width: calc((100% - 5 * 150px) / 3);
      }

      &.license-device-limit {
        width: 150px;
      }

      &.license-activations {
        width: 150px;
      }

      &.license-refresh {
        width: 150px;
      }

      &.license-issued {
        width: 150px;
      }

      &.license-valid-thru {
        width: 150px;
      }
    }
  }
}
</style>
